import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SquareArrowOutUpRight } from 'lucide-react'
import { TokenIcon } from './holdings-table'
import { AddressField } from './address-field'
import { Spinner } from './spinner'
import { useToast } from './toast'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { PositionSnapshot } from '@/lib/api-types'
import { formatAmount, formatSol, formatUsd } from '@/lib/format'
import { CardFigure } from './card-header'
import { tokenLogo } from '@/lib/default-tokens'
import { useInvalidatePortfolio } from '@/lib/portfolio'

const STATUS_LABEL: Record<PositionSnapshot['status'], string> = {
  funding: 'Funding',
  funded: 'Funded',
  buying: 'Buying',
  open: 'Open',
  selling: 'Selling',
  collecting: 'Collecting from vault',
  custodying: 'Sending to vault',
  returning: 'Returning',
  closed: 'Closed',
  failed: 'Failed',
  stalled: 'Delayed',
}
const BUSY = new Set<string>(['funding', 'funded', 'buying', 'selling', 'returning'])

/** The wallet's private positions: each one a separate Relay-funded wallet holding one token. */
export function PositionsCard({ positions, onSell }: { positions: PositionSnapshot[]; onSell: (position: PositionSnapshot) => void }) {
  const trpc = useTRPC()
  const toast = useToast()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  const refresh = () => {
    void invalidatePortfolio()
    void queryClient.invalidateQueries({ queryKey: trpc.position.list.queryKey() })
  }
  const close = useMutation(trpc.position.close.mutationOptions({ onSuccess: refresh, onError: (e) => toast('error', 'Could not close the position', errorMessage(e)) }))
  const retry = useMutation(trpc.position.retry.mutationOptions({ onSuccess: refresh, onError: (e) => toast('error', 'Could not retry', errorMessage(e)) }))

  return (
    <section className="card">
      <div className="flex items-center gap-x-2 px-4 py-4 sm:gap-x-3 sm:px-6">
        <h2 className="card-title">Positions</h2>
        <span className="card-divider" aria-hidden="true" />
        <CardFigure usd={positions.reduce((sum, p) => sum + p.valueUsd, 0)} />
      </div>
      <div className="border-t border-line">
        <ul className="divide-y divide-line">
          {positions.map((p) => {
            const symbol = (p.token.symbol ?? 'token').toUpperCase()
            const busy = BUSY.has(p.status)
            const hasToken = p.tokenBalance > 0
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-4 text-sm sm:gap-4 sm:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <TokenIcon symbol={symbol} imageUrl={tokenLogo(p.token.address, p.token.imageUrl)} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      {symbol}
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === 'open' ? 'bg-accent-soft text-accent' : p.status === 'failed' ? 'bg-negative/15 text-negative' : 'bg-surface-2 text-muted'}`}>
                        {STATUS_LABEL[p.status]}
                      </span>
                      {p.kind === 'strategy' || p.kind === 'vaultStrategy' ? <span className="text-xs text-muted">DCA</span> : null}
                    </div>
                    <div className="mt-1 max-w-xs"><AddressField address={p.positionAddress} short /></div>
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div>{hasToken ? `${formatAmount(p.tokenBalance)} ${symbol}` : `${formatSol(p.sol)} SOL`}</div>
                  <div className="text-xs text-muted">{formatUsd(p.valueUsd)}{hasToken && p.sol > 0.0001 ? ` + ${formatSol(p.sol)} SOL` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {busy ? (
                    <span className="flex items-center gap-2 text-xs text-muted"><Spinner className="size-4" /> {STATUS_LABEL[p.status]}…</span>
                  ) : null}
                  {p.status === 'open' && hasToken ? (
                    <button type="button" className="btn btn-primary !py-1.5" onClick={() => onSell(p)}>Sell</button>
                  ) : null}
                  {!busy && !hasToken && p.sol > 0.0001 ? (
                    <button type="button" className="btn btn-secondary !py-1.5" disabled={close.isPending} onClick={() => close.mutate({ id: p.id })}>Return SOL</button>
                  ) : null}
                  {p.status === 'failed' || p.status === 'stalled' ? (
                    <button type="button" className="btn btn-secondary !py-1.5" disabled={retry.isPending} onClick={() => retry.mutate({ id: p.id })}>Retry</button>
                  ) : null}
                  <a className="btn btn-ghost !p-1" href={`https://solscan.io/account/${p.positionAddress}`} target="_blank" rel="noreferrer" title="View position wallet"><SquareArrowOutUpRight className="size-4" /></a>
                </div>
                {p.lastError ? <div className="w-full text-xs text-negative">{p.lastError}</div> : null}
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
